import { AutocompleteWithLabel } from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
  useQuery
} from '@apollo/client'
import { Button, Grow, Typography } from '@material-ui/core'
import { FC, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import AmountSlider, { AmountRange } from '../../components/SliderWithLabel'
import TimePeriodForm from '../../components/TimePeriodForm'
import { WALLET_QUERY } from '../../graphql/wallet'
import { CREDITORS_BRIEF_QUERY } from '../../graphql/walletCreditors'
import { DateObject } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { Wallet } from '../../types/wallet'
import { CreditorBrief } from '../../types/walletCreditor'
import {
  BalanceType,
  PaymentStatus,
  SortBy,
  TransactionStatus
} from '../../types/walletEnums'
import {
  TransactionFilterInput,
  TransactionResponse
} from '../../types/walletTransaction'
import useStyles from './styles/TransationHistoryFilterBox.styles'

type TransactionHistoryFilterBoxProps = {
  filters: TransactionFilterInput
  setfilters: (v: TransactionFilterInput) => void
  setPage: (v: number) => void
  getTransactions: LazyQueryExecFunction<
    {
      transactions: TransactionResponse
    },
    OperationVariables
  >
  loadingForTransactions: boolean
  sortBy: SortBy
}

export const TransactionHistoryFilterBox: FC<
  TransactionHistoryFilterBoxProps
> = ({
  filters,
  setfilters,
  getTransactions,
  loadingForTransactions,
  setPage,
  sortBy
}) => {
  const classes = useStyles()
  const params = useParams<{ walletId: string }>()
  const { data: wallet } = useQuery<{
    wallet: Wallet
  }>(WALLET_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      walletID: params.walletId
    }
  })
  const [autoCompleteLabelForCreditor, setautoCompleteLabelForCreditor] =
    useState<any>(null)
  const [getCreditors, { data: creditorsData, loading: loadingForCreditors }] =
    useLazyQuery<{
      creditorsBrief: CreditorBrief[]
    }>(CREDITORS_BRIEF_QUERY, { notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (wallet?.wallet.isBilateral) {
      getCreditors({
        variables: {
          walletID: params.walletId
        }
      })
    }
  }, [wallet])
  const [
    autoCompleteLabelForTransactionTypes,
    setautoCompleteLabelForTransactionTypes
  ] = useState<any>(null)
  const [
    autoCompleteLabelForTransactionStatus,
    setautoCompleteLabelForTransactionStatus
  ] = useState<any>(null)
  const [
    autoCompleteLabelForBalanceTypes,
    setautoCompleteLabelForBalanceTypes
  ] = useState<any>(null)
  const [
    autoCompleteLabelForTransactionPaymentStatus,
    setautoCompleteLabelForTransactionPaymentStatus
  ] = useState<any>(null)
  const [reservationTimeDatePickerIsOpen, setreservationTimeDatePickerIsOpen] =
    useState(false)

  const [reservationTimeDateObject, setreservationTimeDateObject] =
    useState<DateObject>({
      gregorianStartDate: filters.transactionTime?.from || '',
      gregorianEndDate: filters.transactionTime?.to || ''
    })

  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const transactionTypes = [
    'credit',
    'debit',
    'reserve',
    'penalty',
    'cashback'
  ] as const
  const transactionStatuses: TransactionStatus[] = [
    'pending',
    'completed',
    'cancelled'
  ]

  const balanceTypes: BalanceType[] = ['walletBalance', 'creditBalance']
  const transactionPaymentStatuses: PaymentStatus[] = [
    'paid',
    'overdue',
    'upcoming',
    'current',
    'pendingPayment'
  ]
  const datePickerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node) &&
        reservationTimeDatePickerIsOpen
      ) {
        setreservationTimeDatePickerIsOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, reservationTimeDatePickerIsOpen])
  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.filterContainer}>
          {wallet?.wallet.isBilateral && (
            <div>
              <AutocompleteWithLabel
                label={getTranslate('Creditor')}
                placeholder={getTranslate('Select Creditor')}
                includeInputInList
                fullWidth
                onChange={(event, v: any) => {
                  if (v) {
                    setautoCompleteLabelForCreditor({
                      value: v.value,
                      label: v.label,
                      code: v.code
                    })
                    setfilters({
                      ...filters,
                      creditorID: v.value
                    })
                  } else {
                    setautoCompleteLabelForCreditor({
                      value: '',
                      label: '',
                      code: ''
                    })
                    setfilters({
                      ...filters,
                      creditorID: undefined
                    })
                  }
                }}
                getOptionSelected={(option, value) =>
                  option.value === value.value && option.label === value.label
                }
                getOptionLabel={(option) => option.label || ''}
                options={
                  creditorsData
                    ? creditorsData?.creditorsBrief.map((item) => ({
                        label: item.entity.entityName,
                        value: item.creditorID.toString() || ''
                      }))
                    : []
                }
                value={autoCompleteLabelForCreditor}
                renderOption={(option) => <>{option.label}</>}
                size="small"
                inputSize="small"
                loading={loadingForCreditors}
                loadingText={getTranslate('Please wait...')}
              />
            </div>
          )}
          <div className={classes.threeColumnRow}>
            <AutocompleteWithLabel
              options={transactionTypes.map((type) => ({
                label: type,
                value: type,
                id: type
              }))}
              inputSize="small"
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) =>
                option.label
                  .toLowerCase()
                  .includes(value.label.toLowerCase()) === true
              }
              multiple
              value={autoCompleteLabelForTransactionTypes}
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const temp = v.map((item: any) => ({
                    label: item.label,
                    value: item.value,
                    id: item.id
                  }))
                  setautoCompleteLabelForTransactionTypes(temp)
                  setfilters({
                    ...filters,
                    type: v.map((walletType: any) => walletType.value)
                  })
                } else {
                  setautoCompleteLabelForTransactionTypes([])
                  setfilters({
                    ...filters,
                    type: undefined
                  })
                }
              }}
              label={getTranslate('type')}
            />
            <AutocompleteWithLabel
              options={transactionStatuses.map((status) => ({
                label: status,
                value: status,
                id: status
              }))}
              inputSize="small"
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) =>
                option.label.toLowerCase().includes(value.label.toLowerCase())
              }
              multiple
              value={autoCompleteLabelForTransactionStatus}
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const temp = v.map((item: any) => ({
                    label: item.label,
                    value: item.value,
                    id: item.id
                  }))
                  setautoCompleteLabelForTransactionStatus(temp)
                  setfilters({
                    ...filters,
                    status: v.map((status: any) => status.value)
                  })
                } else {
                  setautoCompleteLabelForTransactionStatus([])
                  setfilters({
                    ...filters,
                    status: undefined
                  })
                }
              }}
              label={getTranslate('status')}
            />
            <div ref={datePickerRef}>
              <Typography>{getTranslate('Transaction Time')}</Typography>
              <TimePeriodForm
                isOpen={reservationTimeDatePickerIsOpen}
                setTimePickerSelected={setreservationTimeDatePickerIsOpen}
                dateObject={reservationTimeDateObject}
                onChange={(data: DateObject) => {
                  setreservationTimeDateObject(data)
                  setfilters({
                    ...filters,
                    transactionTime: {
                      from: data.gregorianStartDate || '',
                      to: data.gregorianEndDate || ''
                    }
                  })
                }}
                onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
                setOnDoneClickForCloseDatePicker={
                  setOnDoneClickForCloseDatePicker
                }
                enableAllDays
              />
            </div>
          </div>
          <div className={classes.threeColumnRow}>
            <AutocompleteWithLabel
              options={balanceTypes.map((type) => ({
                label: type,
                value: type,
                id: type
              }))}
              inputSize="small"
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) =>
                option.label.toLowerCase().includes(value.label.toLowerCase())
              }
              value={autoCompleteLabelForBalanceTypes}
              onChange={(_, v: any) => {
                if (v) {
                  setautoCompleteLabelForBalanceTypes({
                    value: v.value,
                    label: v.label,
                    code: v.code
                  })
                  setfilters({
                    ...filters,
                    usedBalanceType: v.value as BalanceType
                  })
                } else {
                  setautoCompleteLabelForBalanceTypes(null)
                  setfilters({
                    ...filters,
                    usedBalanceType: undefined
                  })
                }
              }}
              label={getTranslate('Effected Balance')}
            />
            <AutocompleteWithLabel
              options={transactionPaymentStatuses.map((status) => ({
                label: status,
                value: status,
                id: status
              }))}
              inputSize="small"
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) =>
                option.label.toLowerCase().includes(value.label.toLowerCase())
              }
              multiple
              value={autoCompleteLabelForTransactionPaymentStatus}
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const temp = v.map((item: any) => ({
                    label: item.label,
                    value: item.value,
                    id: item.id
                  }))
                  setautoCompleteLabelForTransactionPaymentStatus(temp)
                  setfilters({
                    ...filters,
                    paymentStatus: v.map((status: any) => status.value)
                  })
                } else {
                  setautoCompleteLabelForTransactionPaymentStatus([])
                  setfilters({
                    ...filters,
                    paymentStatus: undefined
                  })
                }
              }}
              label={getTranslate('Payment Status')}
            />

            <AmountSlider
              max={100000}
              min={0}
              onChange={(newValue: AmountRange) =>
                setfilters({
                  ...filters,
                  amountRange: {
                    from: newValue.low,
                    to: newValue.high
                  }
                })
              }
              value={{
                high: filters.amountRange?.to || 100000,
                low: filters.amountRange?.from || 0
              }}
              label={'Amount'}
              currency={getTranslate('IRR')}
            />
          </div>
        </div>
        <div className={classes.ButtonsContainer}>
          <Button
            className={classes.resetButton}
            variant="text"
            color="secondary"
            onClick={() => {
              setfilters({
                ...filters,
                amountRange: undefined,
                creditorID: undefined,
                page: 1,
                paymentStatus: undefined,
                status: undefined,
                transactionTime: undefined,
                type: undefined,
                usedBalanceType: undefined
              })
              setautoCompleteLabelForBalanceTypes(null)
              setautoCompleteLabelForTransactionPaymentStatus(null)
              setautoCompleteLabelForTransactionStatus(null)
              setautoCompleteLabelForTransactionTypes(null)
              setreservationTimeDateObject({})
            }}>
            {getTranslate('Reset')}
          </Button>
          <Button
            className={classes.applyButton}
            variant="contained"
            color="primary"
            disabled={loadingForTransactions}
            onClick={() => {
              getTransactions({
                variables: {
                  filters: filters,
                  sortBy: sortBy
                }
              })
              setPage(1)
            }}>
            {getTranslate('Apply')}
          </Button>
        </div>
      </div>
    </Grow>
  )
}
